import { useMemo } from 'react'

import { useThreadUsers } from '@shared/messaging/src/hooks'
import { UserRole } from '@shared/utils'

/**
 * Resolves the out-of-office notice a thread renders as its last message.
 *
 * `blocking` mirrors the BE rules, which only cover out-of-office providers, MAs and support:
 * a regular thread blocks when any of them is a participant, an internal thread (where only
 * mentions are checked) when every participant is one of them. Patient conversations never
 * block, so the notice stays informational there.
 *
 * @param {Object} thread - the resolved conversation, or undefined while loading
 * @param {Object} [options]
 * @param {Object} [options.user] - recipient of a thread that does not exist yet
 * @param {boolean} [options.internal] - whether the thread is an internal (mention-based) chat
 * @returns {{users: Array, blocking: boolean}|null}
 */
export default function useOutOfOfficeNotice(thread, { user, internal = false } = {}) {
  const threadUsers = useThreadUsers(thread)

  // Falls back to the recipient while the thread is loading, and for a conversation the
  // BE has not created yet (messaging a user straight from their profile).
  const users = threadUsers.length > 0 ? threadUsers : [user].filter(Boolean)

  // `users` is a fresh array every render but fully derived from `thread` and `user`, so
  // keying off those keeps the result stable instead of recomputing on every render.
  return useMemo(() => {
    const usersOut = users.filter((u) => u.outOfOffice)
    if (usersOut.length === 0) return null

    const hasPatient = users.some((u) => u.role === UserRole.Patient)
    const isBlockedOut = (u) => u.outOfOffice && [UserRole.Provider, UserRole.MA, UserRole.Support].includes(u.role)
    const blocked = internal ? users.every(isBlockedOut) : users.some(isBlockedOut)

    return { users: usersOut, blocking: !hasPatient && blocked }
  }, [thread, user, internal]) // eslint-disable-line react-hooks/exhaustive-deps
}
